const ROUTE_TOP_LOADER_ACTIVE_PROGRESS = 0.72;
const ROUTE_TOP_LOADER_COMPLETE_PROGRESS = 1;
const ROUTE_TOP_LOADER_SETTLE_MS = 220;
const ROUTE_TOP_LOADER_MAX_VISIBLE_MS = 8_000;

export type RouteTopLoaderPhase = 'idle' | 'active' | 'settling';

export type RouteTopLoaderState = {
    phase: RouteTopLoaderPhase;
    progress: number;
};

export type RouteTopLoaderSubscriber = (state: RouteTopLoaderState) => void;

export interface RouteTopLoaderHandle {
    done: () => void;
    start: () => void;
}

const IDLE_ROUTE_TOP_LOADER_STATE: RouteTopLoaderState = {
    phase: 'idle',
    progress: 0,
};

let routeTopLoaderState = IDLE_ROUTE_TOP_LOADER_STATE;
let routeTopLoaderSettleTimer: ReturnType<typeof setTimeout> | null = null;
let routeTopLoaderMaxVisibleTimer: ReturnType<typeof setTimeout> | null = null;

const routeTopLoaderSubscribers = new Set<RouteTopLoaderSubscriber>();

const clearRouteTopLoaderTimer = (timer: ReturnType<typeof setTimeout> | null) => {
    if (timer) {
        clearTimeout(timer);
    }
};

const emitRouteTopLoaderState = (state: RouteTopLoaderState) => {
    routeTopLoaderState = state;
    routeTopLoaderSubscribers.forEach((subscriber) => subscriber(state));
};

const completeRouteTopLoader = () => {
    clearRouteTopLoaderTimer(routeTopLoaderSettleTimer);
    clearRouteTopLoaderTimer(routeTopLoaderMaxVisibleTimer);
    routeTopLoaderMaxVisibleTimer = null;

    if (routeTopLoaderState.phase === 'idle') {
        return;
    }

    emitRouteTopLoaderState({
        phase: 'settling',
        progress: ROUTE_TOP_LOADER_COMPLETE_PROGRESS,
    });

    routeTopLoaderSettleTimer = setTimeout(() => {
        routeTopLoaderSettleTimer = null;
        emitRouteTopLoaderState(IDLE_ROUTE_TOP_LOADER_STATE);
    }, ROUTE_TOP_LOADER_SETTLE_MS);
};

const startRouteTopLoader = () => {
    clearRouteTopLoaderTimer(routeTopLoaderSettleTimer);
    clearRouteTopLoaderTimer(routeTopLoaderMaxVisibleTimer);
    routeTopLoaderSettleTimer = null;

    emitRouteTopLoaderState({
        phase: 'active',
        progress: ROUTE_TOP_LOADER_ACTIVE_PROGRESS,
    });

    routeTopLoaderMaxVisibleTimer = setTimeout(() => {
        routeTopLoaderMaxVisibleTimer = null;
        completeRouteTopLoader();
    }, ROUTE_TOP_LOADER_MAX_VISIBLE_MS);
};

export const routeTopLoaderController: RouteTopLoaderHandle & {
    getSnapshot: () => RouteTopLoaderState;
    subscribe: (subscriber: RouteTopLoaderSubscriber) => () => void;
} = {
    done: completeRouteTopLoader,
    getSnapshot: () => routeTopLoaderState,
    start: startRouteTopLoader,
    subscribe: (subscriber) => {
        routeTopLoaderSubscribers.add(subscriber);
        subscriber(routeTopLoaderState);

        return () => {
            routeTopLoaderSubscribers.delete(subscriber);
        };
    },
};
